"use client";

import { cn } from "@universe/ui/lib/utils";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useLayoutEffect, useState } from "react";

import { useDashboardHeader } from "@/providers";

import PendingItemsBadge from "./pending-items-badge";

type TabType = "activity" | "processing" | "pending";

/** Soft band expand — keep in sync with the transition below. */
const TABS_EXPAND_MS = 640;

interface ActivityTabsProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  unconfirmedCount: number;
  processingCount: number;
  /**
   * True while count queries have not resolved yet. While pending we assume
   * the compact no-tabs layout (hr) so the page does not reserve tab height
   * and then shrink when counts are empty.
   */
  isCountsPending?: boolean;
  className?: string;
  showBadge?: boolean;
  onBadgeClick?: () => void;
  onBadgeDismiss?: () => void;
  disableScrollToContent?: boolean;
}

export default function ActivityTabs({
  activeTab,
  onTabChange,
  unconfirmedCount,
  processingCount,
  isCountsPending = false,
  className,
  showBadge = false,
  onBadgeClick,
  onBadgeDismiss,
  disableScrollToContent = false,
}: ActivityTabsProps) {
  const tCommon = useTranslations("common");
  const tPayments = useTranslations("payments");
  const { listHeaderTop } = useDashboardHeader();

  // Handle tab change with scroll to content (only if scrolled down)
  const handleTabChange = (tab: TabType) => {
    if (!disableScrollToContent) {
      const contentElement = document.getElementById("dashboard-tab-content");
      if (contentElement) {
        const elementPosition = contentElement.getBoundingClientRect().top;
        // Only scroll if the content has been scrolled past (element is above its natural position)
        // A small threshold (10px) prevents unnecessary scrolling for minor differences
        const bannerHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--s21-banner-height"
          ) || "0",
          10
        );
        const totalHeaderTop = listHeaderTop + bannerHeight;
        if (elementPosition < totalHeaderTop - 10) {
          const offsetPosition =
            window.scrollY + elementPosition - totalHeaderTop;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }
    }
    onTabChange(tab);
  };

  const hasTabCounts = unconfirmedCount > 0 || processingCount > 0;
  // Assume no tabs until counts are known — avoids skeleton height → shrink.
  const showTabs = !isCountsPending && hasTabCounts;

  // Two-frame expand so grid-template-rows actually transitions. `expanded`
  // gates on `showTabs` so a collapse is reflected during render (no empty-band
  // flash) and the enter animation replays next time tabs appear.
  const [tabsExpanded, setTabsExpanded] = useState(false);
  const expanded = showTabs && tabsExpanded;

  useLayoutEffect(() => {
    if (!showTabs) return;

    // Paint the collapsed (0fr) state first, then flip to expanded (1fr) on a
    // later frame so grid-template-rows actually transitions. Reset on cleanup
    // (async — not a synchronous setState in the effect body) so it replays.
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTabsExpanded(true);
      });
    });
    return () => {
      cancelAnimationFrame(frameId);
      setTabsExpanded(false);
    };
  }, [showTabs]);

  return (
    <div>
      {/* Tabs band — single soft height + opacity motion, no per-tab stagger */}
      <div
        className={cn(
          "grid ease-in-out motion-reduce:transition-none",
          "transition-[grid-template-rows]",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
        style={{ transitionDuration: `${TABS_EXPAND_MS}ms` }}
      >
        <div className="min-h-0 overflow-hidden">
          {showTabs && (
            <div
              className={cn(
                "my-1 pb-2 ease-in-out motion-reduce:transition-none motion-reduce:opacity-100",
                "transition-opacity",
                expanded ? "opacity-100" : "opacity-0",
                className
              )}
              style={{ transitionDuration: `${TABS_EXPAND_MS}ms` }}
            >
              {showBadge && onBadgeClick && (
                <PendingItemsBadge
                  unconfirmedCount={unconfirmedCount}
                  onClick={onBadgeClick}
                  isVisible={showBadge}
                  isTabActive={activeTab === "pending"}
                  onDismiss={onBadgeDismiss}
                />
              )}
              <div className="flex items-center gap-6 border-b border-line">
                {/* Activity Tab */}
                <button
                  onClick={() => handleTabChange("activity")}
                  className={cn(
                    "relative pb-3 px-1 font-medium md:text-base transition-colors hover:cursor-pointer",
                    activeTab === "activity"
                      ? "text-primary"
                      : "text-muted-foreground hover:text-gray-700"
                  )}
                >
                  {tCommon("activity")}
                  {activeTab === "activity" && (
                    <motion.div
                      layoutId="activityTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 40,
                      }}
                    />
                  )}
                </button>

                {/* Processing Payments Tab */}
                {processingCount > 0 && (
                  <button
                    onClick={() => handleTabChange("processing")}
                    className={cn(
                      "relative pb-3 px-1 font-medium text-base transition-colors flex items-center gap-2 hover:cursor-pointer",
                      activeTab === "processing"
                        ? "text-primary"
                        : "text-muted-foreground hover:text-gray-700"
                    )}
                  >
                    <span>{tPayments("processingPayments")}</span>
                    <span
                      className={cn(
                        "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-orange-200 text-orange-800"
                      )}
                    >
                      {processingCount}
                    </span>
                    {activeTab === "processing" && (
                      <motion.div
                        layoutId="activityTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 40,
                        }}
                      />
                    )}
                  </button>
                )}

                {/* Pending Payments Tab */}
                {unconfirmedCount > 0 && (
                  <button
                    onClick={() => {
                      handleTabChange("pending");
                      if (onBadgeDismiss) {
                        onBadgeDismiss();
                      }
                    }}
                    className={cn(
                      "relative pb-3 px-1 font-medium text-base transition-colors flex items-center gap-2 hover:cursor-pointer",
                      activeTab === "pending"
                        ? "text-primary"
                        : "text-muted-foreground hover:text-gray-700"
                    )}
                  >
                    <span>{tPayments("pendingPayments")}</span>
                    <span
                      className={cn(
                        "inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-red-200 text-red-800"
                      )}
                    >
                      {unconfirmedCount}
                    </span>
                    {activeTab === "pending" && (
                      <motion.div
                        layoutId="activityTab"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 40,
                        }}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compact divider — fades with the same soft timing */}
      <div
        className={cn(
          "grid ease-in-out motion-reduce:transition-none",
          "transition-[grid-template-rows,opacity]",
          showTabs
            ? "grid-rows-[0fr] opacity-0 pointer-events-none"
            : "grid-rows-[1fr] opacity-100"
        )}
        style={{ transitionDuration: `${TABS_EXPAND_MS}ms` }}
        aria-hidden={showTabs}
      >
        <div className="min-h-0 overflow-hidden">
          <hr className="hidden md:block md:my-4 opacity-65" />
        </div>
      </div>
    </div>
  );
}
